/**
 * FormBuilderDrawer — full-screen editor for a QR Studio lead form with a live
 * phone-frame preview. The preview IS the public page: both render through
 * LeadFormRenderer, so what the builder shows is exactly what a visitor gets.
 *
 * Left pane sections:
 *   Branding     — title, headline, intro, accent color, logo, LO identity
 *   Fields       — add/reorder/edit fields; name/email/phone presets come
 *                  pre-mapped to CRM contact fields
 *   After Submit — thank-you message | redirect URL | handoff to one of the
 *                  LO's Origin booking pages (prefilled name/email/phone)
 *   CRM Settings — tags (fires TAG_ADDED nurture triggers), lead source,
 *                  create-pipeline-deal toggle, LO notification toggle
 *
 * Saves through the manageLeadForms callable (service role) — slugs and field
 * definitions are re-validated server-side.
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Center,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftAddon,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Radio,
  RadioGroup,
  Select,
  Spinner,
  Stack,
  Switch,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  Textarea,
  Tooltip,
  useToast,
  useColorModeValue,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiTrash2,
  FiArrowUp,
  FiArrowDown,
  FiUpload,
  FiSmartphone,
  FiMonitor,
  FiPlay,
} from 'react-icons/fi';
import { supabase } from '../../../utils/supabase';
import { firebase } from '../../../utils/init-firebase';
import LeadFormRenderer from '../../LeadForm/LeadFormRenderer';
import TestFormModal from './TestFormModal';
import { call, draftToPreviewRow, slugifyName } from './qrStudioUtils';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

// No "Full Name" preset on purpose: a single name box can't be split reliably
// (multi-word last names, suffixes), so names are always collected First/Last.
const FIELD_PRESETS = [
  { name: 'First Name', field: { type: 'text', label: 'First Name', placeholder: 'Jane', mapTo: 'firstName', required: true } },
  { name: 'Last Name', field: { type: 'text', label: 'Last Name', placeholder: 'Smith', mapTo: 'lastName', required: true } },
  { name: 'Email', field: { type: 'email', label: 'Email', placeholder: 'jane@email.com', mapTo: 'email', required: true } },
  { name: 'Phone', field: { type: 'phone', label: 'Phone', placeholder: '(555) 555-5555', mapTo: 'phone', required: false } },
  { name: 'Short Answer', field: { type: 'text', label: 'Your question here', placeholder: '', mapTo: null, required: false } },
  { name: 'Long Answer', field: { type: 'textarea', label: 'Tell us more', placeholder: '', mapTo: null, required: false } },
  { name: 'Dropdown', field: { type: 'select', label: 'Pick an option', placeholder: 'Select one', mapTo: null, required: false, options: ['Option 1', 'Option 2'] } },
  { name: 'Checkbox', field: { type: 'checkbox', label: 'Enter me in the raffle', placeholder: 'Yes, count me in', mapTo: null, required: false } },
  { name: 'Date', field: { type: 'date', label: 'Preferred date', placeholder: '', mapTo: null, required: false } },
  { name: 'Notes for the team', field: { type: 'textarea', label: 'Anything else we should know?', placeholder: '', mapTo: 'notes', required: false } },
];

const MAP_TO_OPTIONS = [
  { value: '', label: 'Not mapped (shows in lead note)' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'notes', label: 'Notes' },
];

function newFieldId() {
  return `fld-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function FieldEditor({ field, index, total, onChange, onMove, onRemove, borderColor }) {
  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={3}>
      <HStack justify="space-between" mb={2}>
        <HStack spacing={2}>
          <Tag size="sm" variant="subtle" colorScheme="gray">{field.type}</Tag>
          {field.mapTo && <Tag size="sm" colorScheme="green">CRM: {field.mapTo}</Tag>}
        </HStack>
        <HStack spacing={0.5}>
          <IconButton icon={<FiArrowUp />} size="xs" variant="ghost" aria-label="Move up" isDisabled={index === 0} onClick={() => onMove(index, -1)} />
          <IconButton icon={<FiArrowDown />} size="xs" variant="ghost" aria-label="Move down" isDisabled={index === total - 1} onClick={() => onMove(index, 1)} />
          <IconButton icon={<FiTrash2 />} size="xs" variant="ghost" colorScheme="red" aria-label="Remove" onClick={() => onRemove(index)} />
        </HStack>
      </HStack>
      <Stack spacing={2}>
        <Input
          size="sm"
          value={field.label}
          onChange={(e) => onChange(index, { label: e.target.value })}
          placeholder="Field label"
        />
        <HStack spacing={2}>
          <Input
            size="sm"
            value={field.placeholder || ''}
            onChange={(e) => onChange(index, { placeholder: e.target.value })}
            placeholder={field.type === 'checkbox' ? 'Checkbox text' : 'Placeholder text'}
          />
          <Select
            size="sm"
            w="210px"
            flexShrink={0}
            value={field.mapTo || ''}
            onChange={(e) => onChange(index, { mapTo: e.target.value || null })}
          >
            {MAP_TO_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </Select>
        </HStack>
        {field.type === 'select' && (
          <Textarea
            size="sm"
            rows={3}
            value={(field.options || []).join('\n')}
            onChange={(e) => onChange(index, { options: e.target.value.split('\n') })}
            placeholder={'One option per line'}
          />
        )}
        <HStack>
          <Switch
            size="sm"
            isChecked={field.required === true}
            onChange={(e) => onChange(index, { required: e.target.checked })}
          />
          <Text fontSize="sm" color="gray.600">Required</Text>
        </HStack>
      </Stack>
    </Box>
  );
}

/** Tag picker backed by the crm_tags registry (global + target user's own). */
function TagPicker({ targetUserId, selected, onChange }) {
  const [allTags, setAllTags] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('crm_tags')
        .select('value, label, color, visibility, owner_id, owner_ids')
        .eq('is_active', true)
        .eq('entity_type', 'contact')
        .or(`visibility.eq.global,owner_id.eq.${targetUserId},owner_ids.cs.{${targetUserId}}`)
        .order('label');
      if (!cancelled) setAllTags(data || []);
    })();
    return () => { cancelled = true; };
  }, [targetUserId]);

  const suggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return allTags
      .filter((t) => !selected.includes(t.value) &&
        ((t.label || '').toLowerCase().includes(q) || (t.value || '').toLowerCase().includes(q)))
      .slice(0, 8);
  }, [search, allTags, selected]);

  return (
    <Box>
      {selected.length > 0 && (
        <Wrap mb={2}>
          {selected.map((value) => {
            const tag = allTags.find((t) => t.value === value);
            return (
              <WrapItem key={value}>
                <Tag size="sm" colorScheme={tag?.color || 'blue'} borderRadius="full">
                  <TagLabel>{tag?.label || value}</TagLabel>
                  <TagCloseButton onClick={() => onChange(selected.filter((v) => v !== value))} />
                </Tag>
              </WrapItem>
            );
          })}
        </Wrap>
      )}
      <Input
        size="sm"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search tags to add..."
      />
      {suggestions.length > 0 && (
        <Box borderWidth="1px" borderRadius="md" mt={1} maxH="180px" overflowY="auto">
          {suggestions.map((tag) => (
            <Box
              key={tag.value}
              px={3}
              py={1.5}
              fontSize="sm"
              cursor="pointer"
              _hover={{ bg: 'blue.50' }}
              onClick={() => { onChange([...selected, tag.value]); setSearch(''); }}
            >
              {tag.label || tag.value}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default function FormBuilderDrawer({ isOpen, onClose, targetUserId, initialDraft, bookingPages, onSaved }) {
  const toast = useToast();
  const [draft, setDraft] = useState(initialDraft);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewDevice, setPreviewDevice] = useState('mobile'); // mobile | desktop
  const [isTestOpen, setIsTestOpen] = useState(false);
  const logoInputRef = useRef(null);
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const panelBg = useColorModeValue('gray.50', 'gray.900');

  // Re-seed the draft whenever the drawer opens for a different form
  useEffect(() => {
    if (isOpen) setDraft(initialDraft);
  }, [isOpen, initialDraft]);

  const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
  const isEdit = !!draft?.id;

  const updateField = (index, patch) => {
    setDraft((prev) => {
      const fields = [...prev.fields];
      fields[index] = { ...fields[index], ...patch };
      return { ...prev, fields };
    });
  };

  const moveField = (index, delta) => {
    setDraft((prev) => {
      const fields = [...prev.fields];
      const target = index + delta;
      if (target < 0 || target >= fields.length) return prev;
      [fields[index], fields[target]] = [fields[target], fields[index]];
      return { ...prev, fields };
    });
  };

  const removeField = (index) => {
    setDraft((prev) => ({ ...prev, fields: prev.fields.filter((_, i) => i !== index) }));
  };

  const addField = (preset) => {
    setDraft((prev) => ({
      ...prev,
      fields: [...prev.fields, { id: newFieldId(), options: [], ...preset.field }],
    }));
  };

  const handleLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({ title: 'Invalid file type', description: 'Use PNG, JPEG, or WebP.', status: 'warning', duration: 3000 });
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast({ title: 'File too large', description: 'Logos must be under 5 MB.', status: 'warning', duration: 3000 });
      return;
    }
    setUploading(true);
    try {
      const path = `lead-forms/${targetUserId}/${Date.now()}_${file.name}`;
      const storageRef = firebase.storage().ref(path);
      await storageRef.put(file);
      const downloadUrl = await storageRef.getDownloadURL();
      set({ logoUrl: downloadUrl });
    } catch (err) {
      toast({ title: 'Upload failed', description: err.message, status: 'error', duration: 4000 });
    } finally {
      setUploading(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    if (!draft.title.trim()) {
      toast({ title: 'Give the form a title', status: 'warning', duration: 3000 });
      return;
    }
    const hasContactField = draft.fields.some((f) => f.mapTo === 'email' || f.mapTo === 'phone');
    if (!hasContactField) {
      toast({
        title: 'Add an Email or Phone field',
        description: 'Submissions need at least one way to reach the lead.',
        status: 'warning',
        duration: 5000,
      });
      return;
    }
    if (draft.postSubmitType === 'booking' && !draft.postSubmitBookingPageId) {
      toast({ title: 'Pick a booking page', description: 'Or switch the after-submit action.', status: 'warning', duration: 4000 });
      return;
    }
    if (draft.postSubmitType === 'redirect' && !/^https?:\/\//i.test(draft.postSubmitRedirectUrl.trim())) {
      toast({ title: 'Enter a valid redirect URL', description: 'Must start with https://', status: 'warning', duration: 4000 });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        userId: targetUserId,
        action: 'save',
        form: {
          id: draft.id || undefined,
          title: draft.title.trim(),
          slug: draft.slug || undefined,
          isActive: draft.isActive,
          headline: draft.headline,
          introText: draft.introText,
          accentColor: draft.accentColor,
          logoUrl: draft.logoUrl,
          loName: draft.loName,
          loTitle: draft.loTitle,
          loNmls: draft.loNmls,
          buttonText: draft.buttonText,
          consentText: draft.consentText,
          fields: draft.fields.map((f) => ({
            ...f,
            options: (f.options || []).map((o) => o.trim()).filter(Boolean),
          })),
          postSubmitType: draft.postSubmitType,
          postSubmitMessage: draft.postSubmitMessage,
          postSubmitRedirectUrl: draft.postSubmitRedirectUrl,
          postSubmitBookingPageId: draft.postSubmitBookingPageId,
          crmCreateDeal: draft.crmCreateDeal,
          crmTags: draft.crmTags,
          crmSource: draft.crmSource,
          notifyOnSubmission: draft.notifyOnSubmission,
        },
      };
      const result = await call('manageLeadForms', payload);
      toast({ title: isEdit ? 'Form saved' : 'Form created', status: 'success', duration: 3000 });
      onSaved(result.form);
    } catch (err) {
      toast({ title: 'Save failed', description: err.message, status: 'error', duration: 6000 });
    } finally {
      setSaving(false);
    }
  };

  if (!draft) return null;

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="full" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader borderBottomWidth="1px" py={3}>
          <HStack justify="space-between">
            <HStack spacing={3}>
              <Text>{isEdit ? 'Edit Form' : 'New Form'}</Text>
              {draft.slug && (
                <Text fontSize="sm" color="gray.400" fontFamily="mono" fontWeight="normal">
                  fhl.one/f/{draft.slug}
                </Text>
              )}
            </HStack>
            <HStack>
              <Tooltip label="Walk through the form exactly as a visitor would — nothing reaches the CRM" hasArrow>
                <Button
                  variant="outline"
                  colorScheme="purple"
                  leftIcon={<FiPlay />}
                  onClick={() => setIsTestOpen(true)}
                  isDisabled={draft.fields.length === 0}
                >
                  Test form
                </Button>
              </Tooltip>
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button colorScheme="blue" onClick={handleSave} isLoading={saving}>
                {isEdit ? 'Save changes' : 'Create form'}
              </Button>
            </HStack>
          </HStack>
        </DrawerHeader>
        <DrawerBody p={0}>
          <Flex h="100%" align="stretch">
            {/* Editor pane */}
            <Box flex="1" maxW={{ base: '100%', lg: '560px' }} overflowY="auto" p={5} borderRightWidth={{ lg: '1px' }}>
              <Accordion defaultIndex={[0, 1]} allowMultiple>
                {/* Branding */}
                <AccordionItem>
                  <AccordionButton px={0}>
                    <Box flex="1" textAlign="left" fontWeight="semibold">Branding</Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel px={0}>
                    <Stack spacing={3}>
                      <FormControl isRequired>
                        <FormLabel fontSize="sm">Form title (internal)</FormLabel>
                        <Input value={draft.title} onChange={(e) => set({ title: e.target.value })} placeholder='e.g. "Home Expo Raffle"' />
                      </FormControl>
                      {isEdit && (
                        <FormControl>
                          <FormLabel fontSize="sm">Public URL</FormLabel>
                          <InputGroup size="sm">
                            <InputLeftAddon>fhl.one/f/</InputLeftAddon>
                            <Input value={draft.slug} onChange={(e) => set({ slug: slugifyName(e.target.value) })} />
                          </InputGroup>
                          <FormHelperText>Careful — changing this breaks links already in the wild. QR codes keep working.</FormHelperText>
                        </FormControl>
                      )}
                      <FormControl>
                        <FormLabel fontSize="sm">Headline (public)</FormLabel>
                        <Input value={draft.headline} onChange={(e) => set({ headline: e.target.value })} placeholder="Let's connect" />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Intro text</FormLabel>
                        <Textarea rows={2} value={draft.introText} onChange={(e) => set({ introText: e.target.value })} />
                      </FormControl>
                      <HStack align="end" spacing={4}>
                        <FormControl w="auto">
                          <FormLabel fontSize="sm">Accent color</FormLabel>
                          <Input
                            type="color"
                            value={draft.accentColor}
                            onChange={(e) => set({ accentColor: e.target.value })}
                            w="64px"
                            h="38px"
                            p={1}
                            cursor="pointer"
                          />
                        </FormControl>
                        <FormControl flex="1">
                          <FormLabel fontSize="sm">Logo (optional)</FormLabel>
                          <HStack>
                            <input type="file" ref={logoInputRef} accept={ALLOWED_TYPES.join(',')} style={{ display: 'none' }} onChange={handleLogoFile} />
                            <Button size="sm" variant="outline" leftIcon={uploading ? <Spinner size="xs" /> : <FiUpload />} onClick={() => logoInputRef.current?.click()} isDisabled={uploading}>
                              {draft.logoUrl ? 'Replace' : 'Upload'}
                            </Button>
                            {draft.logoUrl && (
                              <Button size="sm" variant="ghost" colorScheme="red" onClick={() => set({ logoUrl: '' })}>Remove</Button>
                            )}
                          </HStack>
                        </FormControl>
                      </HStack>
                      <HStack spacing={3}>
                        <FormControl>
                          <FormLabel fontSize="sm">Loan officer name</FormLabel>
                          <Input value={draft.loName} onChange={(e) => set({ loName: e.target.value })} />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="sm">Title</FormLabel>
                          <Input value={draft.loTitle} onChange={(e) => set({ loTitle: e.target.value })} />
                        </FormControl>
                        <FormControl w="140px" flexShrink={0}>
                          <FormLabel fontSize="sm">NMLS #</FormLabel>
                          <Input value={draft.loNmls} onChange={(e) => set({ loNmls: e.target.value })} />
                        </FormControl>
                      </HStack>
                      <FormControl>
                        <FormLabel fontSize="sm">Button text</FormLabel>
                        <Input value={draft.buttonText} onChange={(e) => set({ buttonText: e.target.value })} maxW="240px" />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Consent text</FormLabel>
                        <Textarea rows={2} value={draft.consentText} onChange={(e) => set({ consentText: e.target.value })} />
                      </FormControl>
                    </Stack>
                  </AccordionPanel>
                </AccordionItem>

                {/* Fields */}
                <AccordionItem>
                  <AccordionButton px={0}>
                    <Box flex="1" textAlign="left" fontWeight="semibold">
                      Fields <Text as="span" color="gray.400" fontWeight="normal">({draft.fields.length})</Text>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel px={0}>
                    <Stack spacing={3}>
                      {draft.fields.map((field, index) => (
                        <FieldEditor
                          key={field.id}
                          field={field}
                          index={index}
                          total={draft.fields.length}
                          onChange={updateField}
                          onMove={moveField}
                          onRemove={removeField}
                          borderColor={borderColor}
                        />
                      ))}
                      <Menu>
                        <MenuButton as={Button} size="sm" leftIcon={<FiPlus />} variant="outline" alignSelf="start">
                          Add field
                        </MenuButton>
                        <MenuList maxH="320px" overflowY="auto">
                          {FIELD_PRESETS.map((preset) => (
                            <MenuItem key={preset.name} onClick={() => addField(preset)}>{preset.name}</MenuItem>
                          ))}
                        </MenuList>
                      </Menu>
                    </Stack>
                  </AccordionPanel>
                </AccordionItem>

                {/* After submit */}
                <AccordionItem>
                  <AccordionButton px={0}>
                    <Box flex="1" textAlign="left" fontWeight="semibold">After Submit</Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel px={0}>
                    <Stack spacing={3}>
                      <RadioGroup value={draft.postSubmitType} onChange={(value) => set({ postSubmitType: value })}>
                        <Stack spacing={2}>
                          <Radio value="message">Show a thank-you message</Radio>
                          <Radio value="booking">Send them to one of my booking pages</Radio>
                          <Radio value="redirect">Redirect to a custom URL</Radio>
                        </Stack>
                      </RadioGroup>
                      {draft.postSubmitType === 'message' && (
                        <FormControl>
                          <FormLabel fontSize="sm">Thank-you message</FormLabel>
                          <Textarea rows={2} value={draft.postSubmitMessage} onChange={(e) => set({ postSubmitMessage: e.target.value })} />
                        </FormControl>
                      )}
                      {draft.postSubmitType === 'booking' && (
                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Booking page</FormLabel>
                          <Select
                            placeholder={bookingPages.length ? 'Select a booking page' : 'No active booking pages found'}
                            value={draft.postSubmitBookingPageId || ''}
                            onChange={(e) => set({ postSubmitBookingPageId: e.target.value || null })}
                          >
                            {bookingPages.map((page) => (
                              <option key={page.id} value={page.id}>{page.title}</option>
                            ))}
                          </Select>
                          <FormHelperText>
                            Their name, email, and phone carry over — "fill this out, then grab a time with me."
                          </FormHelperText>
                        </FormControl>
                      )}
                      {draft.postSubmitType === 'redirect' && (
                        <FormControl isRequired>
                          <FormLabel fontSize="sm">Redirect URL</FormLabel>
                          <Input type="url" value={draft.postSubmitRedirectUrl} onChange={(e) => set({ postSubmitRedirectUrl: e.target.value })} placeholder="https://..." />
                        </FormControl>
                      )}
                    </Stack>
                  </AccordionPanel>
                </AccordionItem>

                {/* CRM settings */}
                <AccordionItem>
                  <AccordionButton px={0}>
                    <Box flex="1" textAlign="left" fontWeight="semibold">CRM Settings</Box>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel px={0}>
                    <Stack spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm">Apply tags to new contacts</FormLabel>
                        <TagPicker
                          targetUserId={targetUserId}
                          selected={draft.crmTags}
                          onChange={(crmTags) => set({ crmTags })}
                        />
                        <FormHelperText>
                          Tags fire any TAG_ADDED campaign triggers — the hook for auto-enrolling submitters into a nurture campaign.
                        </FormHelperText>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Lead source</FormLabel>
                        <Input
                          value={draft.crmSource}
                          onChange={(e) => set({ crmSource: e.target.value })}
                          placeholder={draft.slug ? `qr-form-${draft.slug}` : `qr-form-${slugifyName(draft.title) || '…'}`}
                          maxW="320px"
                        />
                        <FormHelperText>
                          Stored on the contact/deal; campaign triggers can filter on it. Leave blank and a source
                          unique to this form is generated automatically{draft.slug ? ` (qr-form-${draft.slug})` : ''}.
                        </FormHelperText>
                      </FormControl>
                      <HStack justify="space-between">
                        <Box>
                          <Text fontSize="sm" fontWeight="medium">Create a pipeline lead</Text>
                          <Text fontSize="xs" color="gray.500">Also open a deal in Lead - New. Off = contact only (good for raffles/events).</Text>
                        </Box>
                        <Switch isChecked={draft.crmCreateDeal} onChange={(e) => set({ crmCreateDeal: e.target.checked })} />
                      </HStack>
                      <HStack justify="space-between">
                        <Box>
                          <Text fontSize="sm" fontWeight="medium">Notify on submission</Text>
                          <Text fontSize="xs" color="gray.500">Email (and SMS for pipeline leads) to the owner on every submission.</Text>
                        </Box>
                        <Switch isChecked={draft.notifyOnSubmission} onChange={(e) => set({ notifyOnSubmission: e.target.checked })} />
                      </HStack>
                    </Stack>
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </Box>

            {/* Live preview — mobile phone frame or desktop browser frame */}
            <Center flex="1" bg={panelBg} display={{ base: 'none', lg: 'flex' }} p={6}>
              <Box w={previewDevice === 'desktop' ? '100%' : 'auto'} maxW={previewDevice === 'desktop' ? '880px' : undefined}>
                <HStack justify="center" mb={3} spacing={1}>
                  <Button
                    size="xs"
                    leftIcon={<FiSmartphone />}
                    variant={previewDevice === 'mobile' ? 'solid' : 'ghost'}
                    colorScheme={previewDevice === 'mobile' ? 'blue' : 'gray'}
                    onClick={() => setPreviewDevice('mobile')}
                  >
                    Mobile
                  </Button>
                  <Button
                    size="xs"
                    leftIcon={<FiMonitor />}
                    variant={previewDevice === 'desktop' ? 'solid' : 'ghost'}
                    colorScheme={previewDevice === 'desktop' ? 'blue' : 'gray'}
                    onClick={() => setPreviewDevice('desktop')}
                  >
                    Desktop
                  </Button>
                  <Text fontSize="xs" color="gray.400" pl={3}>
                    Live preview — exactly what visitors see
                  </Text>
                </HStack>

                {previewDevice === 'mobile' ? (
                  <Box
                    w="375px"
                    h="690px"
                    borderWidth="10px"
                    borderColor="gray.800"
                    borderRadius="3xl"
                    overflow="hidden"
                    bg="white"
                    shadow="2xl"
                    mx="auto"
                  >
                    <Box h="100%" overflowY="auto">
                      <LeadFormRenderer form={draftToPreviewRow(draft)} onSubmit={async () => {}} isPreview />
                    </Box>
                  </Box>
                ) : (
                  <Box
                    w="100%"
                    h="690px"
                    borderWidth="1px"
                    borderColor="gray.300"
                    borderRadius="xl"
                    overflow="hidden"
                    bg="white"
                    shadow="2xl"
                  >
                    {/* Browser chrome bar */}
                    <HStack px={3} py={2} bg="gray.100" borderBottomWidth="1px" borderColor="gray.200" spacing={2}>
                      <HStack spacing={1.5}>
                        <Box boxSize="10px" borderRadius="full" bg="red.400" />
                        <Box boxSize="10px" borderRadius="full" bg="yellow.400" />
                        <Box boxSize="10px" borderRadius="full" bg="green.400" />
                      </HStack>
                      <Center flex="1">
                        <Box bg="white" borderRadius="md" px={3} py={0.5} fontSize="xs" fontFamily="mono" color="gray.500" borderWidth="1px" borderColor="gray.200">
                          fhl.one/f/{draft.slug || slugifyName(draft.title) || '...'}
                        </Box>
                      </Center>
                      <Box w="52px" />
                    </HStack>
                    <Box h="calc(100% - 37px)" overflowY="auto">
                      <LeadFormRenderer form={draftToPreviewRow(draft)} onSubmit={async () => {}} isPreview />
                    </Box>
                  </Box>
                )}
              </Box>
            </Center>
          </Flex>
        </DrawerBody>
      </DrawerContent>

      <TestFormModal
        isOpen={isTestOpen}
        onClose={() => setIsTestOpen(false)}
        draft={draft}
        bookingPages={bookingPages}
      />
    </Drawer>
  );
}
